// packages/shared/src/services/playerSlotService.ts
import { doc, addDoc, updateDoc, deleteDoc, collection, query, where, getDocs, onSnapshot, DocumentReference, getDoc, writeBatch, QuerySnapshot, DocumentSnapshot, orderBy, limit, startAfter } from 'firebase/firestore';
import { FirebaseService } from './firebaseService';
import { CourtService } from './courtService';
import { PlayerSlot, User, TimeRange, Filters, PlayerRatings, Challenge } from '../types';
import { calculateEndTime, filterPlayerSlots } from '../utils/dateUtils';

export class PlayerSlotService {
  private static instance: PlayerSlotService;
  private firebaseService: FirebaseService;

  private constructor() {
    this.firebaseService = FirebaseService.getInstance();
  }

  public static getInstance(): PlayerSlotService {
    if (!PlayerSlotService.instance) {
      PlayerSlotService.instance = new PlayerSlotService();
    }
    return PlayerSlotService.instance;
  }

  private slotsOverlap(
    start1: Date, 
    duration1: number, 
    start2: Date, 
    duration2: number
  ): boolean {
    const end1 = calculateEndTime(start1, duration1);
    const end2 = calculateEndTime(start2, duration2);
    return start1 < end2 && start2 < end1;
  }

  private formatTimeForError(date: Date): string {
    const hours = date.getUTCHours().toString().padStart(2, '0');
    const minutes = date.getUTCMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  private async validateSlotAvailability(
    userId: string,
    startTime: Date,
    duration: number
  ): Promise<void> {
    const db = this.firebaseService.getFirestore();
    
    const exactTimeQuery = query(
      collection(db, 'playerSlots'),
      where('playerId', '==', userId),
      where('startTime', '==', startTime)
    );
    
    const exactTimeSnapshot = await getDocs(exactTimeQuery);
    if (!exactTimeSnapshot.empty) {
      throw new Error('A slot already exists at this exact time');
    }

    const dayStart = new Date(startTime);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(startTime);
    dayEnd.setHours(23, 59, 59, 999);

    const overlapQuery = query(
      collection(db, 'playerSlots'),
      where('playerId', '==', userId),
      where('startTime', '>=', dayStart),
      where('startTime', '<=', dayEnd)
    );

    const overlapSnapshot = await getDocs(overlapQuery);
    
    for (const doc of overlapSnapshot.docs) {
      const existingSlot = { id: doc.id, ...doc.data() } as PlayerSlot;
      const existingStartTime = existingSlot.startTime instanceof Date 
        ? existingSlot.startTime 
        : (existingSlot.startTime as any).toDate();

      if (this.slotsOverlap(startTime, duration, existingStartTime, existingSlot.duration)) {
        const existingEndTime = calculateEndTime(existingStartTime, existingSlot.duration);
        throw new Error(
          `Slot overlaps with existing slot from ${this.formatTimeForError(existingStartTime)} to ${this.formatTimeForError(existingEndTime)}`
        );
      }
    }
  }

  private validateBatchSlotsInternal(slots: Array<{
    startTime: Date;
    duration: number;
    courtId: string;
    courtName: string;
    city: string;
    country?: string;
  }>): void {
    for (let i = 0; i < slots.length; i++) {
      for (let j = i + 1; j < slots.length; j++) {
        if (this.slotsOverlap(
          slots[i].startTime, 
          slots[i].duration,
          slots[j].startTime, 
          slots[j].duration
        )) {
          throw new Error('Cannot create schedule: Slots within the batch have overlapping times');
        }
      }
    }
  }

  public async createPlayerSlot(
    user: User,
    startTime: Date,
    duration: number,
    courtId: string,
    courtName: string,
    city: string,
    country?: string,
    visibility: 'public' | 'private' = 'public'
  ): Promise<PlayerSlot> {
    await this.validateSlotAvailability(user.uid, startTime, duration);
    
    const db = this.firebaseService.getFirestore();
    
    // Fetch court data for location coordinates and proper country/state
    let location: { coordinates: [number, number] } | undefined;
    let courtCountry = country;
    let courtState: string | undefined;
    
    try {
      const courtService = CourtService.getInstance();
      const court = await courtService.getCourt(courtId);
      
      if (court) {
        // Get location coordinates
        if (court.location?.coordinates) {
          location = {
            coordinates: court.location.coordinates
          };
        }
        
        // Get country - prefer court.location.country, keep existing as fallback
        if (court.location?.country) {
          courtCountry = court.location.country;
        }
        
        // Get state from court.details.state (optional, for US states, Canadian provinces, etc.)
        if (court.details?.state) {
          courtState = court.details.state;
        }
      }
    } catch (error) {
      console.warn(`Could not fetch court data for ${courtId}:`, error);
    }
    
    const playerSlotData: Omit<PlayerSlot, 'id'> = {
      playerId: user.uid,
      playerName: user.name,
      playerLevel: user.preferences.level || 'Not Rated',
      playerRatings: user.preferences.ratings || { utr: null, ntrp: null },
      court: courtName,
      courtId,
      startTime,
      duration,
      city,
      country: courtCountry,
      ...(courtState && { state: courtState }),
      visibility,
      timezone: user.preferences.timezone,
      createdAt: new Date(),
      updatedAt: new Date(),
      ...(location && { location })
    };
    
    const docRef = await addDoc(collection(db, 'playerSlots'), playerSlotData);
    const slot = { id: docRef.id, ...playerSlotData };
    
    return slot;
  }

  public async updatePlayerSlot(
    slotId: string,
    updates: Partial<Omit<PlayerSlot, 'id' | 'playerId'>>
  ): Promise<void> {
    const db = this.firebaseService.getFirestore();
    
    // If courtId is being updated, fetch new coordinates, country, and state
    if (updates.courtId) {
      try {
        const courtService = CourtService.getInstance();
        const court = await courtService.getCourt(updates.courtId);
        
        if (court) {
          // Update location coordinates
          if (court.location?.coordinates) {
            updates.location = {
              coordinates: court.location.coordinates
            };
          } else {
            updates.location = undefined;
          }
          
          // Update country from court.location.country
          if (court.location?.country) {
            updates.country = court.location.country;
          }
          
          // Update state from court.details.state (optional)
          if (court.details?.state) {
            updates.state = court.details.state;
          } else {
            // Remove state if new court doesn't have one
            (updates as any).state = null;
          }
        } else {
          // Court not found - remove location
          updates.location = undefined;
        }
      } catch (error) {
        console.warn(`Could not fetch court data for ${updates.courtId}:`, error);
        // Remove location if we can't fetch court data
        updates.location = undefined;
      }
    }
    
    await updateDoc(doc(db, 'playerSlots', slotId), {
      ...updates,
      updatedAt: new Date()
    });
  }

  public async deletePlayerSlot(slotId: string): Promise<void> {
    const db = this.firebaseService.getFirestore();
    await deleteDoc(doc(db, 'playerSlots', slotId));
  }

  public async getUserSlots(userId: string): Promise<PlayerSlot[]> {
    const db = this.firebaseService.getFirestore();
    const q = query(
      collection(db, 'playerSlots'),
      where('playerId', '==', userId)
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      let playerRatings = data.playerRatings || { utr: null, ntrp: null };
      
      return {
        id: doc.id,
        ...data,
        startTime: data.startTime.toDate(),
        playerRatings,
        visibility: data.visibility || 'public'
      };
    }) as PlayerSlot[];
  }

  public async getSharedCalendarSlots(playerId: string): Promise<PlayerSlot[]> {
    try {
      await this.firebaseService.initializeApp();
      
      const db = this.firebaseService.getFirestore();
      const q = query(
        collection(db, 'playerSlots'),
        where('playerId', '==', playerId)
      );
      
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        let playerRatings = data.playerRatings || { utr: null, ntrp: null };
        
        return {
          id: doc.id,
          ...data,
          startTime: data.startTime?.toDate ? data.startTime.toDate() : new Date(data.startTime),
          playerRatings,
          visibility: data.visibility || 'public'
        };
      }) as PlayerSlot[];
    } catch (error) {
      console.error(`Error getting shared calendar slots for user ${playerId}:`, error);
      throw error;
    }
  }
  
  public async filterAvailableSlots(slots: PlayerSlot[]): Promise<PlayerSlot[]> {
    if (slots.length === 0) {
      return slots;
    }
    
    return slots.filter(slot => {
      const slotData = slot as any;
      return !(slotData.booked === true || slotData.status === 'booked' || slotData.status === 'unavailable');
    });
  }
  
  public async getFilteredPlayerSlotsPaginated(
    startDate: Date,
    endDate: Date,
    filters: Filters,
    desiredCount: number = 20,
    lastDocSnapshot?: DocumentSnapshot | null,
    userCoordinates?: { latitude: number; longitude: number } | null
  ): Promise<{ 
    slots: PlayerSlot[], 
    hasMore: boolean, 
    lastDoc: DocumentSnapshot | null 
  }> {
    const db = this.firebaseService.getFirestore();
    
    console.log('[PlayerSlotService] Filters received:', {
      city: filters.city,
      country: filters.country,
      radius: filters.radius,
      userCoordinates
    });
    
    const accumulatedSlots: PlayerSlot[] = [];
    let currentLastDoc: DocumentSnapshot | null = lastDocSnapshot || null;
    let hasMoreInDatabase = true;
    const batchSize = 50;
    let iterations = 0;
    const maxIterations = 10;
    
    while (accumulatedSlots.length < desiredCount && hasMoreInDatabase && iterations < maxIterations) {
      iterations++;
      
      const constraints: any[] = [
        where('startTime', '>=', startDate),
        where('startTime', '<=', endDate),
        where('visibility', '==', 'public')
      ];
      
      if (filters.level !== 'any') {
        constraints.push(where('playerLevel', '==', filters.level));
      }
      
      if (filters.court !== 'any') {
        constraints.push(where('courtId', '==', filters.court));
      }
      
      // Note: City filtering is handled client-side via radius filtering
      // to avoid issues with exact string matching in Firestore
      
      constraints.push(orderBy('startTime', 'asc'));
      constraints.push(limit(batchSize));
      
      if (currentLastDoc) {
        constraints.push(startAfter(currentLastDoc));
      }
      
      const playerSlotsQuery = query(collection(db, 'playerSlots'), ...constraints);
      const querySnapshot = await getDocs(playerSlotsQuery);
      
      if (querySnapshot.docs.length === 0) {
        hasMoreInDatabase = false;
        break;
      }
      
      currentLastDoc = querySnapshot.docs[querySnapshot.docs.length - 1];
      hasMoreInDatabase = querySnapshot.docs.length === batchSize;
      
      let batchSlots = querySnapshot.docs.map(doc => {
        const data = doc.data();
        let playerRatings = data.playerRatings || { utr: null, ntrp: null };
        
        return {
          id: doc.id,
          ...data,
          startTime: data.startTime.toDate(),
          playerRatings,
          visibility: data.visibility || 'public'
        };
      }) as PlayerSlot[];
      
      console.log('[PlayerSlotService] Fetched batch of', batchSlots.length, 'slots');
      if (batchSlots.length > 0) {
        console.log('[PlayerSlotService] Sample slot cities/countries:', 
          batchSlots.slice(0, 3).map(s => ({ city: s.city, country: s.country }))
        );
      }
      
      batchSlots = await this.filterAvailableSlots(batchSlots);
      
      // Determine filtering mode based on priority:
      // 1. Court filter (most specific) - ignore all location filters
      // 2. City/Country filter - use name-based filtering, ignore geo
      // 3. No filters - use geo-based filtering with radius
      
      const hasCourtFilter = filters.court && filters.court !== 'any';
      const hasCityOrCountryFilter = (filters.city && filters.city.trim() !== '') || 
                                      (filters.country && filters.country !== 'any');
      
      // Apply radius filtering ONLY if no court/city/country filter is specified
      if (!hasCourtFilter && !hasCityOrCountryFilter && filters.radius && userCoordinates && typeof filters.radius === 'number' && filters.radius > 0) {
        console.log('[PlayerSlotService] Using radius filtering (no court/city/country specified)');
        batchSlots = this.filterSlotsByRadius(
          batchSlots,
          userCoordinates.latitude,
          userCoordinates.longitude,
          filters.radius
        );
      } else if (hasCourtFilter) {
        console.log('[PlayerSlotService] Using court filter (most specific - ignoring location filters)');
      } else if (hasCityOrCountryFilter) {
        console.log('[PlayerSlotService] Using city/country name filtering (ignoring geo)');
      }
      
      batchSlots = batchSlots.filter(slot => {
        if (filters.duration !== 'any') {
          const requestedDuration = typeof filters.duration === 'string'
            ? parseInt(filters.duration, 10)
            : filters.duration;
          if (requestedDuration > 0 && slot.duration !== requestedDuration) {
            return false;
          }
        }
        
        // City filtering - only apply if no court filter
        if (!hasCourtFilter && filters.city && filters.city.trim() !== '' && slot.city !== filters.city) {
          console.log('[PlayerSlotService] City mismatch - Filter:', filters.city, 'Slot:', slot.city);
          return false;
        }
        
        // Country filtering - only apply if no court filter
        if (!hasCourtFilter && filters.country && filters.country !== 'any' && slot.country !== filters.country) {
          console.log('[PlayerSlotService] Country mismatch - Filter:', filters.country, 'Slot:', slot.country);
          return false;
        }
        
        if (filters.utrMin !== null || filters.utrMax !== null) {
          const playerUTR = slot.playerRatings?.utr;
          if ((playerUTR === null || playerUTR === undefined)) {
            if (filters.utrMin !== null) return false;
          } else {
            if (filters.utrMin !== null && playerUTR < filters.utrMin) return false;
            if (filters.utrMax !== null && playerUTR > filters.utrMax) return false;
          }
        }
        
        if (filters.ntrpMin !== null || filters.ntrpMax !== null) {
          const playerNTRP = slot.playerRatings?.ntrp;
          if ((playerNTRP === null || playerNTRP === undefined)) {
            if (filters.ntrpMin !== null) return false;
          } else {
            if (filters.ntrpMin !== null && playerNTRP < filters.ntrpMin) return false;
            if (filters.ntrpMax !== null && playerNTRP > filters.ntrpMax) return false;
          }
        }
        
        return true;
      });
      
      console.log('[PlayerSlotService] After filtering, batch has', batchSlots.length, 'slots');
      
      accumulatedSlots.push(...batchSlots);
    }
    
    const finalSlots = accumulatedSlots.slice(0, desiredCount);
    const hasMore = accumulatedSlots.length > desiredCount || hasMoreInDatabase;
    
    return { 
      slots: finalSlots, 
      hasMore, 
      lastDoc: currentLastDoc 
    };
  }

  public async getFilteredPlayerSlotsLight(
    startDate: Date,
    endDate: Date,
    filters: Filters,
    timeRange: TimeRange | null = null,
    currentUserId: string | null = null,
    userCoordinates?: { latitude: number; longitude: number } | null
  ): Promise<PlayerSlot[]> {
    const db = this.firebaseService.getFirestore();
    
    let playerSlotsQuery = query(
      collection(db, 'playerSlots'),
      where('startTime', '>=', startDate),
      where('startTime', '<=', endDate),
      where('visibility', '==', 'public')
    );
    
    if (filters.level !== 'any') {
      playerSlotsQuery = query(playerSlotsQuery, where('playerLevel', '==', filters.level));
    }
    
    if (filters.court !== 'any') {
      playerSlotsQuery = query(playerSlotsQuery, where('courtId', '==', filters.court));
    }
    
    if (filters.city && filters.city.trim() !== '') {
      playerSlotsQuery = query(playerSlotsQuery, where('city', '==', filters.city));
    }
    
    const querySnapshot = await getDocs(playerSlotsQuery);
    
    let slots = querySnapshot.docs.map(doc => {
      const data = doc.data();
      let playerRatings = data.playerRatings || { utr: null, ntrp: null };
      
      return {
        id: doc.id,
        ...data,
        startTime: data.startTime.toDate(),
        playerRatings,
        visibility: data.visibility || 'public'
      };
    }) as PlayerSlot[];
    
    slots = await this.filterAvailableSlots(slots);
    
    if (filters.radius && userCoordinates && typeof filters.radius === 'number' && filters.radius > 0) {
      slots = this.filterSlotsByRadius(
        slots,
        userCoordinates.latitude,
        userCoordinates.longitude,
        filters.radius
      );
    }
    
    slots = slots.filter(slot => {
      if (filters.duration !== 'any') {
        const requestedDuration = typeof filters.duration === 'string'
          ? parseInt(filters.duration, 10)
          : filters.duration;
        
        if (requestedDuration > 0 && slot.duration !== requestedDuration) {
          return false;
        }
      }
      
      if (filters.country && filters.country !== 'any' && slot.country !== filters.country) {
        return false;
      }
      
      if (filters.utrMin !== null || filters.utrMax !== null) {
        const playerUTR = slot.playerRatings?.utr;
        
        if ((playerUTR === null || playerUTR === undefined)) {
          if (filters.utrMin !== null) {
            return false;
          }
        } else {
          if (filters.utrMin !== null && playerUTR < filters.utrMin) {
            return false;
          }
          if (filters.utrMax !== null && playerUTR > filters.utrMax) {
            return false;
          }
        }
      }
      
      if (filters.ntrpMin !== null || filters.ntrpMax !== null) {
        const playerNTRP = slot.playerRatings?.ntrp;
        
        if ((playerNTRP === null || playerNTRP === undefined)) {
          if (filters.ntrpMin !== null) {
            return false;
          }
        } else {
          if (filters.ntrpMin !== null && playerNTRP < filters.ntrpMin) {
            return false;
          }
          if (filters.ntrpMax !== null && playerNTRP > filters.ntrpMax) {
            return false;
          }
        }
      }
      
      if (timeRange) {
        const slotStartMs = slot.startTime.getTime();
        const slotEndMs = slotStartMs + (slot.duration * 60 * 1000);
        const rangeStartMs = timeRange.startDate.getTime();
        const rangeEndMs = timeRange.endDate.getTime();
        
        return slotStartMs < rangeEndMs && slotEndMs > rangeStartMs;
      }

      return true;
    });

    return slots;
  }

  public async enrichSlotsWithCoordinatesBatch(
    slots: PlayerSlot[],
    batchSize: number = 20
  ): Promise<PlayerSlot[]> {
    const courtService = CourtService.getInstance();
    const courtCache = new Map<string, any>();
    const enrichedSlots: PlayerSlot[] = [];
    
    for (let i = 0; i < slots.length; i += batchSize) {
      const batch = slots.slice(i, i + batchSize);
      
      const enrichedBatch = await Promise.all(
        batch.map(async (slot) => {
          try {
            let court = courtCache.get(slot.courtId);
            
            if (!court) {
              court = await courtService.getCourt(slot.courtId);
              if (court) {
                courtCache.set(slot.courtId, court);
              }
            }
            
            if (court?.location?.coordinates) {
              return {
                ...slot,
                location: {
                  coordinates: court.location.coordinates
                }
              };
            }
            
            return slot;
          } catch (error) {
            return slot;
          }
        })
      );
      
      enrichedSlots.push(...enrichedBatch);
    }
    
    return enrichedSlots;
  }
  
  public async getFilteredPlayerSlots(
    startDate: Date,
    endDate: Date,
    filters: Filters,
    timeRange: TimeRange | null = null,
    currentUserId: string | null = null,
    userCoordinates?: { latitude: number; longitude: number } | null
  ): Promise<PlayerSlot[]> {
    let slots = await this.getFilteredPlayerSlotsLight(
      startDate,
      endDate,
      filters,
      timeRange,
      currentUserId
    );
    
    if (filters.radius && userCoordinates) {
      slots = await this.enhanceSlotsWithCoordinates(slots);
      slots = this.filterSlotsByRadius(
        slots,
        userCoordinates.latitude,
        userCoordinates.longitude,
        filters.radius
      );
    }

    return slots;
  }

  private async enhanceSlotsWithCoordinates(slots: PlayerSlot[]): Promise<PlayerSlot[]> {
    const courtService = CourtService.getInstance();
    const courtCache = new Map<string, any>();
    
    const enhancedSlots = await Promise.all(
      slots.map(async (slot) => {
        try {
          let court = courtCache.get(slot.courtId);
          
          if (!court) {
            court = await courtService.getCourt(slot.courtId);
            if (court) {
              courtCache.set(slot.courtId, court);
            }
          }
          
          if (court?.location?.coordinates) {
            return {
              ...slot,
              location: {
                coordinates: court.location.coordinates
              }
            };
          }
          
          return slot;
        } catch (error) {
          return slot;
        }
      })
    );
    
    return enhancedSlots;
  }

  public async getSlotsForTimeRange(timeRange: TimeRange): Promise<PlayerSlot[]> {
    const db = this.firebaseService.getFirestore();
    
    const q = query(
      collection(db, 'playerSlots'),
      where('startTime', '>=', timeRange.startDate),
      where('startTime', '<=', timeRange.endDate),
      where('visibility', '==', 'public')
    );
    
    const querySnapshot = await getDocs(q);
    
    let slots = querySnapshot.docs.map(doc => {
      const data = doc.data();
      let playerRatings = data.playerRatings || { utr: null, ntrp: null };
      
      return {
        id: doc.id,
        ...data,
        startTime: data.startTime.toDate(),
        playerRatings,
        visibility: data.visibility || 'public'
      };
    }) as PlayerSlot[];
    
    return await this.filterAvailableSlots(slots);
  }

  public async getSlotById(slotId: string): Promise<PlayerSlot | null> {
    const db = this.firebaseService.getFirestore();
    const slotDocRef = doc(db, 'playerSlots', slotId);
    const slotDocSnap = await getDoc(slotDocRef);
    
    if (!slotDocSnap.exists()) {
      return null;
    }
    
    const data = slotDocSnap.data();
    let playerRatings = data.playerRatings || { utr: null, ntrp: null };
    
    return {
      id: slotDocSnap.id,
      ...data,
      startTime: data.startTime.toDate(),
      playerRatings,
      visibility: data.visibility || 'public'
    } as PlayerSlot;
  }

  public subscribeToUserSlots(
    userId: string,
    callback: (slots: PlayerSlot[]) => void
  ): () => void {
    const db = this.firebaseService.getFirestore();
    const q = query(
      collection(db, 'playerSlots'),
      where('playerId', '==', userId)
    );
    
    return onSnapshot(q, (snapshot: QuerySnapshot) => {
      const slots = snapshot.docs.map((doc) => {
        const data = doc.data();
        let playerRatings = data.playerRatings || { utr: null, ntrp: null };
        
        return {
          id: doc.id,
          ...data,
          startTime: data.startTime.toDate(),
          playerRatings,
          visibility: data.visibility || 'public'
        };
      }) as PlayerSlot[];
      
      callback(slots);
    });
  }

  public async createScheduleSlots(
    user: User,
    slots: Array<{
      startTime: Date;
      duration: number;
      courtId: string;
      courtName: string;
      city: string;
      country?: string;
    }>,
    visibility: 'public' | 'private' = 'public'
  ): Promise<PlayerSlot[]> {
    this.validateBatchSlotsInternal(slots);

    for (const slot of slots) {
      try {
        await this.validateSlotAvailability(user.uid, slot.startTime, slot.duration);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'validation failed';
        throw new Error(`Cannot create schedule: Slot at ${slot.startTime.toISOString()} ${errorMessage.toLowerCase()}`);
      }
    }

    const db = this.firebaseService.getFirestore();
    const batch = writeBatch(db);
    const createdSlots: PlayerSlot[] = [];
    
    // Fetch court data (coordinates, country, state) once per unique court
    const courtService = CourtService.getInstance();
    const courtDataCache: Record<string, {
      location?: { coordinates: [number, number] };
      country?: string;
      state?: string;
    } | undefined> = {};

    for (const slot of slots) {
      // Get court data from cache or fetch it
      if (courtDataCache[slot.courtId] === undefined) {
        try {
          const court = await courtService.getCourt(slot.courtId);
          if (court) {
            courtDataCache[slot.courtId] = {
              location: court.location?.coordinates ? {
                coordinates: court.location.coordinates
              } : undefined,
              country: court.location?.country,
              state: court.details?.state
            };
          } else {
            courtDataCache[slot.courtId] = undefined;
          }
        } catch (error) {
          console.warn(`Could not fetch court data for ${slot.courtId}:`, error);
          courtDataCache[slot.courtId] = undefined;
        }
      }
      
      const courtData = courtDataCache[slot.courtId];
      
      const playerSlotData: Omit<PlayerSlot, 'id'> = {
        playerId: user.uid,
        playerName: user.name,
        playerLevel: user.preferences.level || 'Not Rated',
        playerRatings: user.preferences.ratings || { utr: null, ntrp: null },
        court: slot.courtName,
        courtId: slot.courtId,
        startTime: slot.startTime,
        duration: slot.duration,
        city: slot.city,
        country: courtData?.country || slot.country,
        ...(courtData?.state && { state: courtData.state }),
        visibility,
        timezone: user.preferences.timezone,
        createdAt: new Date(),
        updatedAt: new Date(),
        ...(courtData?.location && { location: courtData.location })
      };

      const docRef = doc(collection(db, 'playerSlots'));
      batch.set(docRef, playerSlotData);
      createdSlots.push({ id: docRef.id, ...playerSlotData });
    }

    await batch.commit();
    
    return createdSlots;
  }

  public async updateUserSlotsRatings(
    userId: string, 
    newRatings: PlayerRatings,
    fallbackTimezone?: string
  ): Promise<void> {
    const db = this.firebaseService.getFirestore();
    
    const now = new Date();
    
    const q = query(
      collection(db, 'playerSlots'),
      where('playerId', '==', userId)
    );
    
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return;
    }
    
    const batch = writeBatch(db);
    let updatedCount = 0;
    
    querySnapshot.docs.forEach(doc => {
      const data = doc.data();
      const slotStartTime = data.startTime?.toDate ? data.startTime.toDate() : new Date(data.startTime);
      const slotTimezone = data.timezone || fallbackTimezone || 'UTC';
      
      try {
        const todayInSlotTZ = new Date(now.toLocaleString("en-US", {timeZone: slotTimezone}));
        todayInSlotTZ.setHours(0, 0, 0, 0);
        
        const offsetDiff = now.getTime() - new Date(now.toLocaleString("en-US", {timeZone: slotTimezone})).getTime();
        const cutoffTimeUTC = new Date(todayInSlotTZ.getTime() - offsetDiff);
        
        if (slotStartTime >= cutoffTimeUTC) {
          batch.update(doc.ref, {
            playerRatings: newRatings,
            updatedAt: new Date()
          });
          updatedCount++;
        }
      } catch (error) {
        const todayUTC = new Date();
        todayUTC.setUTCHours(0, 0, 0, 0);
        
        if (slotStartTime >= todayUTC) {
          batch.update(doc.ref, {
            playerRatings: newRatings,
            updatedAt: new Date()
          });
          updatedCount++;
        }
      }
    });
    
    if (updatedCount > 0) {
      await batch.commit();
    }
  }

  public async markSlotAsBooked(slotId: string, bookedBy?: string, challengeId?: string): Promise<void> {
    const db = this.firebaseService.getFirestore();
    const updateData: any = {
      booked: true,
      bookedAt: new Date()
    };
    
    if (bookedBy) {
      updateData.bookedBy = bookedBy;
    }
    
    if (challengeId) {
      updateData.challengeId = challengeId;
    }
    
    await updateDoc(doc(db, 'playerSlots', slotId), updateData);
  }

  public async markSlotAsAvailable(slotId: string): Promise<void> {
    const db = this.firebaseService.getFirestore();
    await updateDoc(doc(db, 'playerSlots', slotId), {
      booked: false,
      bookedAt: null,
      bookedBy: null,
      challengeId: null
    });
  }

  private filterSlotsByRadius(
    slots: PlayerSlot[],
    userLatitude: number,
    userLongitude: number,
    radiusMiles: number
  ): PlayerSlot[] {
    console.log(`[filterSlotsByRadius] User location: ${userLatitude}, ${userLongitude}, Radius: ${radiusMiles} miles`);
    
    return slots.filter(slot => {
      if (!slot.location?.coordinates) {
        console.log(`[filterSlotsByRadius] ✅ Slot ${slot.id} (${slot.court}, ${slot.city}) - NO coordinates, including (any location)`);
        return true;
      }

      const [slotLon, slotLat] = slot.location.coordinates;
      const distance = this.calculateDistance(
        userLatitude,
        userLongitude,
        slotLat,
        slotLon
      );
      
      const included = distance <= radiusMiles;
      console.log(`[filterSlotsByRadius] ${included ? '✅' : '❌'} Slot ${slot.id} (${slot.court}, ${slot.city}) at [${slotLat}, ${slotLon}] - Distance: ${distance.toFixed(2)} miles ${included ? '(within radius)' : '(outside radius)'}`);
      
      return included;
    });
  }

  private calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 3959;
    const dLat = this.toRad(lat2 - lat1);
    const dLon = this.toRad(lon2 - lon1);
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  private toRad(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  public async deleteScheduleSlots(slotIds: string[]): Promise<void> {
    if (slotIds.length === 0) return;
    
    const db = this.firebaseService.getFirestore();
    const batch = writeBatch(db);
    
    slotIds.forEach(slotId => {
      const docRef = doc(db, 'playerSlots', slotId);
      batch.delete(docRef);
    });
    
    await batch.commit();
  }
}

export default PlayerSlotService;